import {
  Box,
  Stack,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  Divider
} from '@mui/material';

export default function ArrimaSpouseForm({
  spouseForm,
  setSpouseForm,
}) {
  // ---------------- Generic Handlers ----------------
  const handleChange = (key) => (e) => {
    setSpouseForm(prev => ({ ...prev, [key]: e.target.value }));
  };

  const handleArrayChange = (arrayName, idx, key) => (e) => {
    setSpouseForm(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) =>
        i === idx ? { ...item, [key]: e.target.value } : item
      )
    }));
  };

  const addArrayItem = (arrayName, template) => {
    setSpouseForm(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], template]
    }));
  };

  const removeArrayItem = (arrayName, idx) => {
    setSpouseForm(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== idx)
    }));
  };

  // Reset dependent fields when parent question changes to "Non"
  const handleDependentChange = (key, dependentFields = []) => (e) => {
    const value = e.target.value;
    setSpouseForm(prev => {
      const updates = { [key]: value };
      if (value === "Non") {
        dependentFields.forEach(field => {
          updates[field] = "";
        });
      }
      return { ...prev, ...updates };
    });
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h4" gutterBottom align="center">
        Déclaration d'intérêt arrima - Conjoint
      </Typography>

      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3 }}>
        Renseignements personnels de votre conjoint(e)
      </Typography>

      {/* Informations personnelles */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Informations personnelles
      </Typography>

      <Stack spacing={3}>
        <TextField
          fullWidth
          required
          label="Nom(s) de famille"
          value={spouseForm.spouseLastNamePassport}
          onChange={handleChange('spouseLastNamePassport')}
        />

        <TextField
          fullWidth
          required
          label="Prénom(s)"
          value={spouseForm.spouseFirstNamePassport}
          onChange={handleChange('spouseFirstNamePassport')}
        />

        <TextField
          fullWidth
          label="Autres noms utilisés"
          value={spouseForm.spouseOtherNamesUsed}
          onChange={handleChange('spouseOtherNamesUsed')}
        />

        <FormControl>
          <FormLabel>Sexe</FormLabel>
          <RadioGroup
            row
            value={spouseForm.spouseSex}
            onChange={handleChange('spouseSex')}
          >
            <FormControlLabel value="Homme" control={<Radio required />} label="Homme" />
            <FormControlLabel value="Femme" control={<Radio required />} label="Femme" />
            <FormControlLabel value="Autre" control={<Radio required />} label="Autre" />
          </RadioGroup>
        </FormControl>

        <TextField
          fullWidth
          required
          label="Date de naissance"
          type="date"
          value={spouseForm.spousePersonalBirthDate}
          onChange={handleChange('spousePersonalBirthDate')}
          InputLabelProps={{ shrink: true }}
        />

        <FormControl>
          <FormLabel>Votre conjoint(e) a-t-il(elle) déjà obtenu un numéro de référence individuel du Ministère ?</FormLabel>
          <RadioGroup
            row
            value={spouseForm.spouseHasMIFIReferenceNumber}
            onChange={handleDependentChange('spouseHasMIFIReferenceNumber', ['spouseMifiReferenceNumber'])}
          >
            <FormControlLabel value="Oui" control={<Radio required />} label="Oui" />
            <FormControlLabel value="Non" control={<Radio required />} label="Non" />
          </RadioGroup>
        </FormControl>

        {spouseForm.spouseHasMIFIReferenceNumber === "Oui" && (
          <TextField
            fullWidth
            required
            label="Si oui, Numéro de référence individuel du Ministère"
            value={spouseForm.spouseMifiReferenceNumber}
            onChange={handleChange('spouseMifiReferenceNumber')}
          />
        )}
      </Stack>

      <Divider sx={{ my: 4 }} />

      {/* Situation actuelle au Québec */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Situation actuelle au Québec
      </Typography>

      <Stack spacing={3}>

        <FormControl fullWidth>
          <FormLabel>
            Quelle est votre situation actuelle au Québec ? <br />
            (Cochez ce qui s’applique parmi les permis de travail, d'études ou statuts de visiteur, etc.)*
          </FormLabel>
          <TextField required
            value={spouseForm.spouseCurrentStatus} onChange={handleChange("spouseCurrentStatus")} />
        </FormControl>

        <TextField
          fullWidth
          required
          label="Numéro du permis actuel"
          value={spouseForm.spousePermitNumber}
          onChange={handleChange('spousePermitNumber')}
        />

        <TextField
          fullWidth
          required
          label="Date de début"
          type="date"
          value={spouseForm.spousePermitStartDate}
          onChange={handleChange('spousePermitStartDate')}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          fullWidth
          required
          label="Date de fin"
          type="date"
          value={spouseForm.spousePermitEndDate}
          onChange={handleChange('spousePermitEndDate')}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      <Divider sx={{ my: 4 }} />

      {/* Scolarité */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Scolarité
      </Typography>

      <Stack spacing={3}>
        <FormControl>
          <FormLabel>Avez-vous un diplôme supérieur aux études primaires ?</FormLabel>
          <RadioGroup
            row
            value={spouseForm.spouseHasEducationAbovePrimary}
            onChange={(e) => {
              const value = e.target.value;
              setSpouseForm(prev => ({
                ...prev,
                spouseHasEducationAbovePrimary: value,
                spouseEducation: value === "Non" ? [{
                  country: "",
                  startDate: "",
                  completionDate: "",
                  officialTitle: "",
                  issuingAuthority: "",
                  domain: "",
                  field: "",
                  speciality: ""
                }] : prev.spouseEducation
              }));
            }}
          >
            <FormControlLabel value="Oui" control={<Radio required />} label="Oui" />
            <FormControlLabel value="Non" control={<Radio required />} label="Non" />
          </RadioGroup>
        </FormControl>


        <FormControl>
          <FormLabel>Avez-vous une Évaluation comparative des études effectuées hors du Québec émise après le 31 janvier 2013 ?</FormLabel>
          <RadioGroup
            row
            value={spouseForm.spouseHasComparativeEvaluation}
            onChange={handleChange('spouseHasComparativeEvaluation')}
          >
            <FormControlLabel value="Oui" control={<Radio required />} label="Oui" />
            <FormControlLabel value="Non" control={<Radio required />} label="Non" />
          </RadioGroup>
        </FormControl>

        {spouseForm.spouseHasEducationAbovePrimary === "Oui" && (
          <Stack spacing={2}>

            {/* Education Records */}
            {spouseForm.spouseEducation.map((edu, index) => (
              <Box key={index} sx={{ border: 1, borderColor: 'grey.300', p: 3, borderRadius: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Diplôme --{index + 1}
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    required
                    label="Pays"
                    value={edu.country}
                    onChange={handleArrayChange('spouseEducation', index, 'country')}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Études commencées le"
                    type="date"
                    value={edu.startDate}
                    onChange={handleArrayChange('spouseEducation', index, 'startDate')}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Études réussies le"
                    type="date"
                    value={edu.completionDate}
                    onChange={handleArrayChange('spouseEducation', index, 'completionDate')}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Titre officiel"
                    value={edu.officialTitle}
                    onChange={handleArrayChange('spouseEducation', index, 'officialTitle')}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Autorité émettrice"
                    value={edu.issuingAuthority}
                    onChange={handleArrayChange('spouseEducation', index, 'issuingAuthority')}
                  />

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Domaine de formation
                  </Typography>

                  <TextField
                    fullWidth
                    required
                    label="Domaine:"
                    value={edu.domain}
                    onChange={handleArrayChange('spouseEducation', index, 'domain')}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Filiere:"
                    value={edu.field}
                    onChange={handleArrayChange('spouseEducation', index, 'field')}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Specialite:"
                    value={edu.speciality}
                    onChange={handleArrayChange('spouseEducation', index, 'speciality')}
                  />

                  <Box>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => removeArrayItem('spouseEducation', index)}
                    >
                      Supprimer ce diplôme
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ))}

            <Box>
              <Button
                variant="outlined"
                onClick={() => addArrayItem('spouseEducation', {
                  country: "",
                  startDate: "",
                  completionDate: "",
                  officialTitle: "",
                  issuingAuthority: "",
                  domain: "",
                  field: "",
                  speciality: ""
                })}
              >
                Ajouter un diplôme
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>

      <Divider sx={{ my: 4 }} />

      {/* Parcours professionnel */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Parcours professionnel
      </Typography>

      <Stack spacing={3}>
        <FormControl>
          <FormLabel>Avez-vous occupé des emplois ou effectué des stages au Québec au cours des cinq dernières années ?</FormLabel>
          <RadioGroup
            row
            value={spouseForm.spouseHasQuebecWorkExperience}
            onChange={(e) => {
              const value = e.target.value;
              setSpouseForm(prev => ({
                ...prev,
                spouseHasQuebecWorkExperience: value,
                spouseWorkExperience: value === "Non" ?
                  [{
                    startDate: "",
                    endDate: "",
                    employmentType: "",
                    jobType: "",
                    jobTitle: "",
                    companyName: "",
                    isOwner: "",
                    country: "",
                    province: "",
                    weeklyHours: "",
                    employerSectorInadmissible: ""
                  }] : prev.spouseWorkExperience
              }));
            }}
          >
            <FormControlLabel value="Oui" control={<Radio required />} label="Oui" />
            <FormControlLabel value="Non" control={<Radio required />} label="Non" />
          </RadioGroup>
        </FormControl>

        {spouseForm.spouseHasQuebecWorkExperience === "Oui" && (
          <>
            <Typography gutterBottom>
              Si Oui inscrivez:
            </Typography>

            {spouseForm.spouseWorkExperience.map((work, index) => (
              <Box key={index} sx={{ border: 1, borderColor: 'grey.300', p: 3, borderRadius: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Emploi {index + 1}
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    required
                    label="Date de début"
                    type="date"
                    value={work.startDate}
                    onChange={handleArrayChange('spouseWorkExperience', index, 'startDate')}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Date de fin"
                    type="date"
                    value={work.endDate}
                    onChange={handleArrayChange('spouseWorkExperience', index, 'endDate')}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Type d’occupation"
                    value={work.jobType}
                    onChange={handleArrayChange('spouseWorkExperience', index, 'jobType')}
                  />

                  <FormControl>
                    <FormLabel>Type d'emploi</FormLabel>
                    <RadioGroup
                      value={work.employmentType}
                      onChange={handleArrayChange("spouseWorkExperience", index, "employmentType")}
                    >
                      <FormControlLabel value="Temps plein" control={<Radio required />} label="Temps plein" />
                      <FormControlLabel value="Temps partiel" control={<Radio required />} label="Temps partiel" />
                      <FormControlLabel value="Stage" control={<Radio required />} label="Stage" />
                      <FormControlLabel value="Congé parental" control={<Radio required />} label="Congé parental" />
                      <FormControlLabel value="Arrêt de travail" control={<Radio required />} label="Arrêt de travail" />
                      <FormControlLabel value="Chômage" control={<Radio required />} label="Chômage" />
                      <FormControlLabel value="Bénévolat" control={<Radio required />} label="Bénévolat" />
                    </RadioGroup>
                  </FormControl>

                  <TextField
                    fullWidth
                    required
                    label="Titre"
                    value={work.jobTitle}
                    onChange={handleArrayChange('spouseWorkExperience', index, 'jobTitle')}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Nom de l'entreprise ou de l'organisme"
                    value={work.companyName}
                    onChange={handleArrayChange('spouseWorkExperience', index, 'companyName')}
                  />

                  <FormControl>
                    <FormLabel>Êtes-vous propriétaire de l'entreprise ?</FormLabel>
                    <RadioGroup
                      row
                      value={work.isOwner}
                      onChange={handleArrayChange('spouseWorkExperience', index, 'isOwner')}
                    >
                      <FormControlLabel value="Oui" control={<Radio required />} label="Oui" />
                      <FormControlLabel value="Non" control={<Radio required />} label="Non" />
                    </RadioGroup>
                  </FormControl>

                  <TextField
                    fullWidth
                    required
                    label="Pays ou territoire"
                    value={work.country}
                    onChange={handleArrayChange('spouseWorkExperience', index, 'country')}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Province ou État"
                    value={work.province}
                    onChange={handleArrayChange('spouseWorkExperience', index, 'province')}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Nombre moyen d'heures travaillées par semaine"
                    type="number"
                    value={work.weeklyHours}
                    onChange={handleArrayChange('spouseWorkExperience', index, 'weeklyHours')}
                  />

                  <FormControl>
                    <FormLabel>Est-ce que votre employeur exploite une entreprise dans un secteur inadmissible visé aux articles 1 ou 2 de l'Annexe E du Règlement sur l'immigration au Québec (RIQ) ?</FormLabel>
                    <RadioGroup
                      value={work.employerSectorInadmissible}
                      onChange={handleArrayChange('spouseWorkExperience', index, 'employerSectorInadmissible')}
                    >
                      <FormControlLabel value="Oui" control={<Radio required />} label="Oui" />
                      <FormControlLabel value="Non" control={<Radio required />} label="Non" />
                    </RadioGroup>
                  </FormControl>

                  <Box>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => removeArrayItem('spouseWorkExperience', index)}
                    >
                      Supprimer cet emploi
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ))}

            <Box>
              <Button
                variant="outlined"
                onClick={() => addArrayItem('spouseWorkExperience', {
                  startDate: "",
                  endDate: "",
                  employmentType: "",
                  jobType: "",
                  jobTitle: "",
                  companyName: "",
                  isOwner: "",
                  country: "",
                  province: "",
                  weeklyHours: "",
                  employerSectorInadmissible: ""
                })}
              >
                Ajouter un emploi
              </Button>
            </Box>
          </>
        )}
      </Stack>

      <Divider sx={{ my: 4 }} />

      {/* Connaissances linguistiques en français */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Connaissances linguistiques en français
      </Typography>

      <Stack spacing={3}>
        <FormControl>
          <FormLabel>Êtes-vous en mesure de démontrer vos connaissances linguistiques en français… obtenus depuis moins de 2 ans ?</FormLabel>
          <RadioGroup
            row
            value={spouseForm.spouseHasFrenchTest}
            onChange={handleDependentChange('spouseHasFrenchTest', [
              'spouseFrenchTestType',
              'spouseFrenchCandidateNumber',
              'spouseFrenchTestDate',
              'spouseFrenchTestCenterName',
              'spouseFrenchTestCenterCity',
              'spouseFrenchTestCenterCountry',
              'spouseFrenchSpeaking',
              'spouseFrenchWriting',
              'spouseFrenchListening',
              'spouseFrenchReading'
            ])}
          >
            <FormControlLabel value="Oui" control={<Radio required />} label="Oui" />
            <FormControlLabel value="Non" control={<Radio required />} label="Non" />
          </RadioGroup>
        </FormControl>

        {spouseForm.spouseHasFrenchTest === "Oui" && (
          <>
            <TextField
              fullWidth
              required
              label="Titre du diplôme ou du test"
              value={spouseForm.spouseFrenchTestType}
              onChange={handleChange('spouseFrenchTestType')}
            />

            <TextField
              fullWidth
              required
              label="Numéro de candidat ou de l'attestation"
              value={spouseForm.spouseFrenchCandidateNumber}
              onChange={handleChange('spouseFrenchCandidateNumber')}
            />

            <TextField
              fullWidth
              required
              label="Date du test"
              type="date"
              value={spouseForm.spouseFrenchTestDate}
              onChange={handleChange('spouseFrenchTestDate')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              required
              label="Nom du centre agréé"
              value={spouseForm.spouseFrenchTestCenterName}
              onChange={handleChange('spouseFrenchTestCenterName')}
            />

            <TextField
              fullWidth
              required
              label="Ville où est situé le centre agréé"
              value={spouseForm.spouseFrenchTestCenterCity}
              onChange={handleChange('spouseFrenchTestCenterCity')}
            />

            <TextField
              fullWidth
              required
              label="Pays ou territoire où est situé le centre agréé"
              value={spouseForm.spouseFrenchTestCenterCountry}
              onChange={handleChange('spouseFrenchTestCenterCountry')}
            />

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Résultats obtenus :
            </Typography>

            <TextField
              fullWidth
              required
              label="Expression orale"
              value={spouseForm.spouseFrenchSpeaking}
              onChange={handleChange('spouseFrenchSpeaking')}
            />

            <TextField
              fullWidth
              required
              label="Expression écrite"
              value={spouseForm.spouseFrenchWriting}
              onChange={handleChange('spouseFrenchWriting')}
            />

            <TextField
              fullWidth
              required
              label="Compréhension orale"
              value={spouseForm.spouseFrenchListening}
              onChange={handleChange('spouseFrenchListening')}
            />

            <TextField
              fullWidth
              required
              label="Compréhension écrite"
              value={spouseForm.spouseFrenchReading}
              onChange={handleChange('spouseFrenchReading')}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
}